import {useContext, useEffect, useRef} from "react";
import {useNavigate} from "react-router";
import {AppShellContext} from "./app-shell-context";

type SwipeTargets = {prev: string | null; next: string | null};

// Touches starting this close to the screen edge belong to the browser's own back/forward swipe.
const edgeGuardPx = 20;
// Movement before deciding whether the gesture is horizontal.
const lockDistancePx = 10;
const commitDistancePx = 80;
const commitVelocityPxPerMs = 0.4;
// The list drag-and-drop (utils/list-dnd/use-drag.ts) starts a touch drag when the finger stays
// within 5px for 100ms. Ignore those touches here, or reordering an item would also switch tabs.
const dndHoldMs = 100;
const dndSlopPx = 5;

const isExcludedTarget = (target: EventTarget | null, shell: HTMLElement) => {
  let node = target instanceof HTMLElement ? target : null;
  while (node && node !== shell) {
    if (node.matches("input, textarea, select, [contenteditable]")) return true;
    if (node.scrollWidth > node.clientWidth) {
      const {overflowX} = getComputedStyle(node);
      if (overflowX === "auto" || overflowX === "scroll") return true;
    }
    node = node.parentElement;
  }
  return false;
};

/**
 * Page-wide horizontal swipe between the bottom tabs. Swiping left goes to `next`, right to
 * `prev`. Uses native listeners on the AppShell element (not React handlers) so touches inside
 * portalled drawers, which bubble through the React tree, don't count.
 */
export const useTabSwipe = (targets: SwipeTargets) => {
  const ctx = useContext(AppShellContext);
  const navigate = useNavigate();
  const targetsRef = useRef(targets);
  targetsRef.current = targets;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    const shell = ctx?.shellRef.current;
    const content = ctx?.contentRef.current;
    if (!shell || !content) return;

    let gesture: {
      startX: number;
      startY: number;
      startTs: number;
      lastX: number;
      lastTs: number;
      velocity: number;
      state: "pending" | "horizontal";
      movedEarly: boolean;
    } | null = null;

    const setOffset = (dx: number, animate: boolean) => {
      content.style.transition = animate ? "transform 150ms ease-out, opacity 150ms ease-out" : "";
      content.style.transform = dx === 0 ? "" : `translateX(${dx}px)`;
      content.style.opacity = dx === 0 ? "" : String(1 - Math.min(Math.abs(dx) / 600, 0.3));
    };

    // Damped offset that follows the finger, with extra resistance when there's no tab that way.
    const offsetFor = (dx: number) => {
      const {prev, next} = targetsRef.current;
      const hasTarget = dx < 0 ? next !== null : prev !== null;
      return dx * (hasTarget ? 0.5 : 0.15);
    };

    const onTouchStart = (e: TouchEvent) => {
      gesture = null;
      if (e.touches.length !== 1) return;
      const t = e.touches[0];
      if (t.clientX < edgeGuardPx || t.clientX > window.innerWidth - edgeGuardPx) return;
      if (isExcludedTarget(e.target, shell)) return;
      const now = e.timeStamp;
      gesture = {
        startX: t.clientX,
        startY: t.clientY,
        startTs: now,
        lastX: t.clientX,
        lastTs: now,
        velocity: 0,
        state: "pending",
        movedEarly: false,
      };
    };

    const onTouchMove = (e: TouchEvent) => {
      if (!gesture) return;
      if (e.touches.length !== 1) {
        gesture = null;
        setOffset(0, true);
        return;
      }
      const t = e.touches[0];
      const dx = t.clientX - gesture.startX;
      const dy = t.clientY - gesture.startY;
      if (gesture.state === "pending") {
        const dist = Math.max(Math.abs(dx), Math.abs(dy));
        if (!gesture.movedEarly) {
          if (e.timeStamp - gesture.startTs >= dndHoldMs) {
            gesture = null;
            return;
          }
          if (dist < dndSlopPx) return;
          gesture.movedEarly = true;
        }
        if (dist < lockDistancePx) return;
        // once the browser scrolls vertically, the gesture is its own
        if (Math.abs(dx) < Math.abs(dy) * 2 || !e.cancelable) {
          gesture = null;
          return;
        }
        gesture.state = "horizontal";
      }
      e.preventDefault();
      const dt = e.timeStamp - gesture.lastTs;
      if (dt > 0) gesture.velocity = (t.clientX - gesture.lastX) / dt;
      gesture.lastX = t.clientX;
      gesture.lastTs = e.timeStamp;
      setOffset(offsetFor(dx), false);
    };

    const onTouchEnd = () => {
      const g = gesture;
      gesture = null;
      if (g?.state !== "horizontal") return;
      const dx = g.lastX - g.startX;
      const isFlick =
        Math.abs(g.velocity) > commitVelocityPxPerMs && Math.sign(g.velocity) === Math.sign(dx);
      const target = dx < 0 ? targetsRef.current.next : targetsRef.current.prev;
      if (target !== null && (Math.abs(dx) > commitDistancePx || isFlick)) {
        setOffset(0, false);
        navigateRef.current(target);
      } else {
        setOffset(0, true);
      }
    };

    const onTouchCancel = () => {
      if (gesture?.state === "horizontal") setOffset(0, true);
      gesture = null;
    };

    shell.addEventListener("touchstart", onTouchStart, {passive: true});
    shell.addEventListener("touchmove", onTouchMove, {passive: false});
    shell.addEventListener("touchend", onTouchEnd);
    shell.addEventListener("touchcancel", onTouchCancel);
    return () => {
      shell.removeEventListener("touchstart", onTouchStart);
      shell.removeEventListener("touchmove", onTouchMove);
      shell.removeEventListener("touchend", onTouchEnd);
      shell.removeEventListener("touchcancel", onTouchCancel);
      setOffset(0, false);
    };
  }, [ctx]);
};
